import { Route } from '@react-navigation/core';
import { Subject } from 'rxjs';
import { isDebugEnabled } from '../../../functions/debugging';
import AsyncStorage, { Key } from '../../../utils/asyncStorage';
import { makeDebug } from '../../../utils/makeDebug';
import { CurrentShow } from '../../showcase/functions/useCurrentShow/useCurrentShow';
import { showInfo } from '../../showcase/functions/useCurrentShow/useCurrentShow.utils';
import {
  OnboardingFlowScene,
  OnboardingFlowScope,
  OnboardingStep,
} from './onboardingSteps';

const debug = makeDebug('onboarding:state', isDebugEnabled('legacyOnboarding'));

const kOptionalStepsActiveFreq = 3;

type SellerDetectionState =
  | {
      status: 'pending';
    }
  | {
      status: 'detected';
      sellerCode: string;
      path?: string;
    }
  | {
      status: 'end';
    };
export class OnboardingStateManager {
  static shared = new OnboardingStateManager();

  private _launchCount = 0;
  private _campaignId: string | null = null;
  private _sellerDetectionState: SellerDetectionState = { status: 'pending' };
  private _isInviteCodeEntered = false;
  private _statsigReadyForUserWithProfile = false;

  /**
   * Keeps track of if onboarding flow was activated for some specific scope
   * and what happened in that flow, based on which, we can determine if
   * the onboarding flow is required in other scopes.
   */
  private _presentationTracings = new Map<
    OnboardingFlowScope,
    Map<OnboardingFlowScene, { requested: boolean; presented: boolean }>
  >();

  /**
   * keeps track of how many times the app is launched w/o the conditions
   * matched in function `matchesCriteriaForDelayOptionals`, which I call
   * `independent launch count`, which means launches w/o any *parameters*.
   */
  private _independentLaunchCount = 0;
  private _isIndependentLaunchUpdated = false;
  private _ready$ = new Subject<void>();
  private _showcaseReady$ = new Subject<void>();
  private _initialRoute: Route<string, any> | undefined = undefined;
  private _isNewUserSignedUp: boolean | undefined = undefined;

  private constructor() {}

  get isNewUserSignup() {
    return this._isNewUserSignedUp;
  }

  getTracingStateForScope(scope: OnboardingFlowScope) {
    return this._presentationTracings.get(scope);
  }

  reset() {
    this._launchCount = 0;
    this._campaignId = null;
    this._sellerDetectionState = { status: 'pending' };
    this._isInviteCodeEntered = false;
    this._presentationTracings = new Map();
    this._independentLaunchCount = 0;
    this._isIndependentLaunchUpdated = false;
    this._isNewUserSignedUp = undefined;
    this._isNewUserSignedUp = undefined;
    this._statsigReadyForUserWithProfile = false;
  }

  async onPreRequestOnboardingSteps() {
    if (this._isIndependentLaunchUpdated) return;

    this._independentLaunchCount =
      (await AsyncStorage.getNumberData(Key.IndependentLaunchCount)) ?? 0;
    if (!this.matchesCriteriaForDelayOptionals()) {
      this._independentLaunchCount++;
      debug(
        'independentLaunchCount updated to: ',
        this._independentLaunchCount,
      );
      await AsyncStorage.storeNumberData(
        Key.IndependentLaunchCount,
        this._independentLaunchCount,
      );
    } else {
      debug('independentLaunchCount: ', this._independentLaunchCount);
    }

    this._isIndependentLaunchUpdated = true;
  }

  matchesCriteriaForDelayOptionals() {
    // see the following notion card for details about this specific logic:
    // https://www.notion.so/popshoplive/Onboarding-Flow-Tweaks-8fe0066b00814218a017fa93cf0fe136

    // 2. When a user clicks on a campaign link (product, live show,
    // upcoming show, etc)
    if (this._campaignId) return true;

    // 3. When a user clicks on a store profile link (i.e.
    // popshop.live/devonleecarlson)
    if (this._sellerDetectionState.status === 'detected') return true;

    // 4. When a user enters an invite code (which comes before???
    // selecting a profile picture)
    if (this._isInviteCodeEntered) return true;

    return false;
  }

  tryNotifyReady() {
    if (this._initialRoute != null && this._statsigReadyForUserWithProfile) {
      this._ready$.next();
    }
  }

  shouldDelayForOptionalSteps() {
    // if the current onboarding matches any criteria, then just delay it
    if (this.matchesCriteriaForDelayOptionals()) return true;

    // if this is not the every N-th attempts, also delay it.
    // return this._independentLaunchCount % kOptionalStepsActiveFreq !== 0;

    // Only show the optional steps on exactly the Nth time app opens
    return this._independentLaunchCount !== kOptionalStepsActiveFreq;
  }

  statsigReadyForUserWithProfile(value: boolean) {
    this._statsigReadyForUserWithProfile = value;
    this.tryNotifyReady();

    if (value) {
      debug('statsig ready for logged in user with profile information');
    } else {
      debug(
        'statsig **NOT** ready for logged in user with profile information',
      );
    }
  }

  shouldDelayForStep(
    scene: OnboardingFlowScene,
    step: OnboardingStep,
    _isAuthRequiredForMobile: boolean,
  ) {
    if (
      step.flow === '_onboarding' &&
      (step.screen === 'categories' || step.screen === 'followStores')
    ) {
      return scene !== 'account-creation' && scene !== 'user-preferences';
    }

    // as requested by @jack, invite code for app-open should only be
    // delayed if and only if either the following two conditions is met:
    // - When a user clicks on a store profile link (i.e. popshop.live/devonleecarlson)
    // - Campaign link clicked (which i believe to be related to this card)
    if (step.flow === '_auth') {
      if (step.screen === 'avatar') {
        // skip avatar step for login that is actually not new user signup.
        return this._isNewUserSignedUp === false;
      }

      if (step.screen === 'inviteCode') {
        return (
          Boolean(this._campaignId) ||
          this._sellerDetectionState.status === 'detected'
        );
      }
    }

    return false;
  }

  appLaunchedWithCounter(counter: number) {
    debug('app launched with counter: %d', counter);
    this._launchCount = counter;
  }

  appLaunchedWithCampaign(campaignId: string) {
    debug('app launched with campaign id: %s', campaignId);
    this._campaignId = campaignId;
  }

  appLaunchedWithInitialRoute(route: Route<string, any>) {
    if (this._initialRoute) return;

    debug('app initial route: %s, params: %O', route.name, route.params);
    this._initialRoute = route;
    this.tryNotifyReady();

    // if the app is launched with showcase, remember this and wait for
    // the show case to be loaded and its type to be determined later
    if (route.name === 'sellerProfile') {
      this._sellerDetectionState = {
        status: 'detected',
        sellerCode: route.params?.sellerUrlCode as any,
        path: route.path,
      };
    }
  }

  async userLoggedIn(isNewUser: boolean | undefined) {
    this._isNewUserSignedUp = isNewUser;
    debug(isNewUser ? 'new user signed up' : 'user logged in ');
    this._independentLaunchCount = 0;
    try {
      await AsyncStorage.storeNumberData(
        Key.IndependentLaunchCount,
        this._independentLaunchCount,
      );
    } catch {}
  }

  showCaseLoaded(show: CurrentShow) {
    if (!showInfo(show).exists) return;

    debug('showcase content loaded: ', show);
    this._showcaseReady$.next();
  }

  /**
   * Call the given callback when content is ready
   * @param callback the callback function to execute
   */
  ready(callback: () => void) {
    debug('ready called');

    const callReadyHandlers = () => {
      debug('ready, calling ready callback handlers... ');
      if (this._initialRoute!.name === 'showcase') {
        const subscription = this._showcaseReady$.subscribe(() => {
          debug('ready to present onboarding flow after showcase being loaded');
          subscription.unsubscribe();
          callback();
        });
      } else {
        debug('ready to present onboarding flow');
        callback();
      }
    };

    if (this._initialRoute && this._statsigReadyForUserWithProfile) {
      callReadyHandlers();
    } else {
      const ready = this._ready$.subscribe(() => {
        ready.unsubscribe();
        callReadyHandlers();
      });
    }
  }

  inviteCodeEntered(code: string) {
    debug('invite coded entered: ', code);
    this._isInviteCodeEntered = true;
  }

  onboardingFlowRequested(
    scope: OnboardingFlowScope,
    scene: OnboardingFlowScene,
  ) {
    debug(`======flow requested for scene ${scene} (scope: ${scope})======`);
    this.onboardingFlowStateChanged(scope, scene, { requested: true });
  }

  onboardingFlowPresented(
    scope: OnboardingFlowScope,
    scene: OnboardingFlowScene,
  ) {
    debug(`flow presented for scene ${scene} (scope: ${scope})`);
    this.onboardingFlowStateChanged(scope, scene, {
      presented: true,
      requested: false,
    });
  }

  onboardingFlowSkipped(
    scope: OnboardingFlowScope,
    scene: OnboardingFlowScene,
  ) {
    debug(`flow skipped for scene ${scene} (scope: ${scope})`);
    const state = this._presentationTracings.get(scope)!.get(scene)!;
    this.onboardingFlowStateChanged(scope, scene, {
      presented: state.presented,
      requested: false,
    });
  }

  isOnboardingFlowRequested(
    scope: OnboardingFlowScope,
    scene: OnboardingFlowScene,
  ) {
    return this._presentationTracings.get(scope)?.get(scene)?.requested;
  }

  hasOnboardingFlowPresented(
    scope: OnboardingFlowScope,
    scene: OnboardingFlowScene,
  ) {
    return this._presentationTracings.get(scope)?.get(scene)?.presented;
  }

  private onboardingFlowStateChanged(
    scope: OnboardingFlowScope,
    scene: OnboardingFlowScene,
    change: { [p in 'requested' | 'presented']?: boolean },
  ) {
    if (!this._presentationTracings.has(scope)) {
      this._presentationTracings.set(scope, new Map());
    }
    const map = this._presentationTracings.get(scope)!;
    const state = {
      ...(map.get(scene) ?? { requested: false, presented: false }),
      ...change,
    };
    map.set(scene, state);
  }
}
